import React from "react";
import {
  Box,
  Checkbox,
  Group,
  LoadingOverlay,
  Pagination,
  ScrollArea,
  Select,
  Stack,
  Table,
  Text,
  UnstyledButton,
} from "@mantine/core";
import {
  IconChevronDown,
  IconChevronUp,
  IconSelector,
} from "@tabler/icons-react";
import styles from "./StandardTable.module.css";

export type StandardTableSortDirection = "asc" | "desc";

export type StandardTableAlign = "left" | "right" | "center";

export interface StandardTableColumn<T> {
  /** Unique identifier and sort key for the column. */
  key: string;
  /** Header label. */
  label?: React.ReactNode;
  /** Cell horizontal alignment. Defaults to "left". */
  align?: StandardTableAlign;
  /** Optional explicit width (px or any CSS value). */
  width?: string | number;
  /** When true, header becomes a sort button. Requires `sortField`/`onSort`. */
  sortable?: boolean;
  /** Render cell content. Falls back to row[key] if omitted. */
  render?: (row: T, index: number) => React.ReactNode;
}

export interface StandardTablePagination {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  pageSizeOptions?: string[];
  /** Show "Showing X to Y of Z" summary. Defaults to true. */
  showSummary?: boolean;
  /** Label for items, e.g. "products". Defaults to "results". */
  itemLabel?: string;
}

export interface StandardTableProps<T> {
  columns: StandardTableColumn<T>[];
  data: T[];
  /** Stable row key extractor. */
  getRowKey: (row: T, index: number) => string | number;

  /** Fired on row click. Renders a pointer cursor and hover highlight. */
  onRowClick?: (row: T) => void;

  /** Optional toolbar (search, filters, action buttons) above the table. */
  toolbar?: React.ReactNode;

  /** Render a custom row instead of the default cell renderer. */
  renderRow?: (row: T, index: number) => React.ReactNode;

  /** Extra rows appended after data rows (e.g. totals). */
  footerRows?: React.ReactNode;

  /** Empty state. Defaults to a centered "No results" message. */
  emptyState?: React.ReactNode;

  /** Loading state — shows a LoadingOverlay over the table area. */
  loading?: boolean;

  /** Sorting integration. */
  sortField?: string | null;
  sortDirection?: StandardTableSortDirection;
  onSort?: (key: string) => void;

  /** Pagination integration. */
  pagination?: StandardTablePagination;

  /** Min width forwarded to ScrollContainer (defaults to undefined). */
  minWidth?: number | string;

  /**
   * Max height forwarded to the native ScrollContainer (px or any CSS value).
   * Bounds the table's own vertical scroll — pair with `stickyHeader` to keep
   * the header pinned while the body scrolls. Only applies when `minWidth` is set.
   */
  maxHeight?: number | string;

  /** Whether to wrap in a bordered Paper-like shell. Defaults to true. */
  withShell?: boolean;

  /** Sticky table header. */
  stickyHeader?: boolean;

  /** Aria label for the table element. */
  ariaLabel?: string;

  /**
   * Enable a leading selection checkbox column. Selection is injected into the
   * default row renderer only (not `renderRow`). Backward compatible — when
   * omitted the table renders exactly as before.
   */
  selectable?: boolean;
  /** Set of currently selected row keys (values produced by `getRowKey`). */
  selectedKeys?: Set<string | number>;
  /** Fired when a single row's checkbox is toggled. */
  onToggleRow?: (row: T, index: number) => void;
  /**
   * When provided and it returns false, the row's checkbox is disabled (unless
   * the row is already selected, so it can always be de-selected). Use this to
   * enforce a maximum-selection cap.
   */
  isRowSelectable?: (row: T) => boolean;
  /** Optional select-all handler; renders an interactive header checkbox. */
  onToggleAll?: () => void;
  /** Header checkbox state when `onToggleAll` is provided. */
  headerSelectionState?: { checked: boolean; indeterminate: boolean };
}

function alignClass(align?: StandardTableAlign) {
  if (align === "right") return styles.alignRight;
  if (align === "center") return styles.alignCenter;
  return styles.alignLeft;
}

export function StandardTable<T>({
  columns,
  data,
  getRowKey,
  onRowClick,
  toolbar,
  renderRow,
  footerRows,
  emptyState,
  loading = false,
  sortField,
  sortDirection = "asc",
  onSort,
  pagination,
  minWidth,
  maxHeight,
  withShell = true,
  stickyHeader = false,
  ariaLabel,
  selectable = false,
  selectedKeys,
  onToggleRow,
  isRowSelectable,
  onToggleAll,
  headerSelectionState,
}: StandardTableProps<T>) {
  const isEmpty = data.length === 0;
  const totalColSpan = columns.length + (selectable ? 1 : 0);

  const selectionHeaderCell = selectable ? (
    <Table.Th key="__selection" className={styles.selectionCell}>
      {onToggleAll ? (
        <Checkbox
          aria-label="Select all rows"
          checked={headerSelectionState?.checked ?? false}
          indeterminate={headerSelectionState?.indeterminate ?? false}
          onChange={onToggleAll}
        />
      ) : null}
    </Table.Th>
  ) : null;

  const headerCells = columns.map((col) => {
    const align = col.align ?? "left";
    const isSortable = !!col.sortable && !!onSort;
    const isSorted = sortField === col.key;
    const SortIcon = !isSorted
      ? IconSelector
      : sortDirection === "asc"
        ? IconChevronUp
        : IconChevronDown;

    const headerInner = isSortable ? (
      <UnstyledButton
        className={styles.sortableHeader}
        onClick={() => onSort?.(col.key)}
        type="button"
      >
        <Text component="span" size="sm" fw={600}>
          {col.label}
        </Text>
        <SortIcon size={14} opacity={isSorted ? 1 : 0.5} />
      </UnstyledButton>
    ) : (
      <Text component="span" size="sm" fw={600}>
        {col.label}
      </Text>
    );

    return (
      <Table.Th
        key={col.key}
        className={alignClass(align)}
        style={col.width ? { width: col.width } : undefined}
      >
        {headerInner}
      </Table.Th>
    );
  });

  const renderDefaultRow = (row: T, idx: number) => {
    const rowKey = getRowKey(row, idx);
    const isSelected = selectedKeys?.has(rowKey) ?? false;
    const rowSelectable = isRowSelectable ? isRowSelectable(row) : true;
    return (
      <Table.Tr
        key={rowKey}
        data-clickable={onRowClick ? "true" : undefined}
        data-selected={isSelected ? "true" : undefined}
        onClick={onRowClick ? () => onRowClick(row) : undefined}
      >
        {selectable ? (
          <Table.Td
            className={styles.selectionCell}
            onClick={(e) => e.stopPropagation()}
          >
            <Checkbox
              aria-label="Select row"
              checked={isSelected}
              disabled={!rowSelectable && !isSelected}
              onChange={() => onToggleRow?.(row, idx)}
            />
          </Table.Td>
        ) : null}
        {columns.map((col) => {
          const align = col.align ?? "left";
          const value = col.render
            ? col.render(row, idx)
            : ((row as Record<string, unknown>)[col.key] as React.ReactNode);
          return (
            <Table.Td key={col.key} className={alignClass(align)}>
              {value}
            </Table.Td>
          );
        })}
      </Table.Tr>
    );
  };

  const tableEl = (
    <Table
      className={styles.table}
      stickyHeader={stickyHeader}
      aria-label={ariaLabel}
    >
      <Table.Thead>
        <Table.Tr>
          {selectionHeaderCell}
          {headerCells}
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {isEmpty ? (
          <Table.Tr>
            <Table.Td colSpan={totalColSpan} className={styles.empty}>
              {emptyState ?? (
                <Text c="dimmed" size="sm">
                  No results to display.
                </Text>
              )}
            </Table.Td>
          </Table.Tr>
        ) : (
          data.map((row, idx) =>
            renderRow ? (
              <React.Fragment key={getRowKey(row, idx)}>
                {renderRow(row, idx)}
              </React.Fragment>
            ) : (
              renderDefaultRow(row, idx)
            ),
          )
        )}
        {footerRows}
      </Table.Tbody>
    </Table>
  );

  const inner = (
    <Box pos="relative">
      <LoadingOverlay
        visible={loading}
        overlayProps={{ blur: 1 }}
        zIndex={20}
      />
      {minWidth ? (
        <Table.ScrollContainer
          minWidth={minWidth}
          maxHeight={maxHeight}
          type="native"
        >
          {tableEl}
        </Table.ScrollContainer>
      ) : (
        <ScrollArea className={styles.scroll} type="auto">
          {tableEl}
        </ScrollArea>
      )}
    </Box>
  );

  return (
    <Stack gap={0} className={withShell ? styles.shell : undefined}>
      {toolbar ? <Box className={styles.toolbar}>{toolbar}</Box> : null}
      {inner}
      {pagination ? <PaginationFooter pagination={pagination} /> : null}
    </Stack>
  );
}

function PaginationFooter({
  pagination,
}: {
  pagination: StandardTablePagination;
}) {
  const {
    page,
    pageSize,
    total,
    onPageChange,
    onPageSizeChange,
    pageSizeOptions,
    showSummary = true,
    itemLabel = "results",
  } = pagination;

  const totalPages = Math.max(1, Math.ceil(total / Math.max(1, pageSize)));
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);

  return (
    <Box className={styles.footer}>
      <Group gap="md" wrap="wrap">
        {showSummary && (
          <Text size="sm" c="dimmed">
            {total === 0
              ? `No ${itemLabel}`
              : `Showing ${start.toLocaleString()}–${end.toLocaleString()} of ${total.toLocaleString()} ${itemLabel}`}
          </Text>
        )}
        {onPageSizeChange && (
          <Select
            aria-label="Page size"
            className={styles.pageSize}
            value={pageSize.toString()}
            onChange={(value) => {
              if (value) onPageSizeChange(parseInt(value, 10));
            }}
            data={pageSizeOptions ?? ["10", "25", "50", "100"]}
            size="sm"
            allowDeselect={false}
          />
        )}
      </Group>
      <Pagination
        total={totalPages}
        value={page}
        onChange={onPageChange}
        size="sm"
        siblings={1}
      />
    </Box>
  );
}

/* ---------- Lower-level shell for fully custom tables ----------
 * Used by tables with row groups / expandable rows where you need
 * direct access to <Table.Thead/Tbody/Tr/Td> but still want the
 * standardized chrome (border, toolbar, footer, scroll).
 */

export interface StandardTableShellProps {
  toolbar?: React.ReactNode;
  footer?: React.ReactNode;
  loading?: boolean;
  withShell?: boolean;
  minWidth?: number | string;
  stickyHeader?: boolean;
  ariaLabel?: string;
  children: React.ReactNode;
}

export function StandardTableShell({
  toolbar,
  footer,
  loading = false,
  withShell = true,
  minWidth,
  stickyHeader = false,
  ariaLabel,
  children,
}: StandardTableShellProps) {
  const tableEl = (
    <Table
      className={styles.table}
      stickyHeader={stickyHeader}
      aria-label={ariaLabel}
    >
      {children}
    </Table>
  );

  return (
    <Stack gap={0} className={withShell ? styles.shell : undefined}>
      {toolbar ? <Box className={styles.toolbar}>{toolbar}</Box> : null}
      <Box pos="relative">
        <LoadingOverlay
          visible={loading}
          overlayProps={{ blur: 1 }}
          zIndex={20}
        />
        {minWidth ? (
          <Table.ScrollContainer minWidth={minWidth} type="native">
            {tableEl}
          </Table.ScrollContainer>
        ) : (
          <ScrollArea className={styles.scroll} type="auto">
            {tableEl}
          </ScrollArea>
        )}
      </Box>
      {footer ? <Box className={styles.footer}>{footer}</Box> : null}
    </Stack>
  );
}

export default StandardTable;
